// The whole of the page's rendering: elements made by hand. Text always goes in as text nodes, so nothing a
// translation or a server answer says can become markup.

const PROPERTIES = new Set(['value', 'checked', 'disabled', 'readOnly', 'hidden']);

/** What h() takes as a child: nodes, text, and null, undefined or false for nothing, nested as deep as it likes. */
export type Child = Node | string | number | boolean | null | undefined | readonly Child[];

/** Attributes and listeners by name: on* functions are listeners, class, style and ref are special. */
export type Props = Record<string, unknown>;

/**
 * An element with its attributes, listeners and children.
 * h('button', { class: 'primary-button', onclick: submit }, t('...')) — null, undefined and false are skipped,
 * both as attribute values and as children; arrays of children are flattened.
 */
export function h<K extends keyof HTMLElementTagNameMap>(tag: K, props?: Props | null, ...children: Child[]): HTMLElementTagNameMap[K];
export function h(tag: string, props?: Props | null, ...children: Child[]): HTMLElement;
export function h(tag: string, props?: Props | null, ...children: Child[]): HTMLElement {
    const element = document.createElement(tag);

    if (props) {
        for (const [key, value] of Object.entries(props)) {
            if (value === undefined || value === null || value === false) continue;

            if (key.startsWith('on') && typeof value === 'function') {
                element.addEventListener(key.slice(2), value as EventListener);
            } else if (key === 'class') {
                element.className = String(value);
            } else if (key === 'style' && typeof value === 'object') {
                Object.assign(element.style, value);
            } else if (key === 'ref' && typeof value === 'function') {
                (value as (element: HTMLElement) => void)(element);
            } else if (PROPERTIES.has(key)) {
                (element as unknown as Record<string, unknown>)[key] = value;
            } else {
                element.setAttribute(key, value === true ? '' : String(value));
            }
        }
    }

    append(element, children);

    return element;
}

function append(parent: Node, children: readonly Child[]): void {
    for (const child of children) {
        if (child === null || child === undefined || child === false) continue;

        if (Array.isArray(child)) {
            append(parent, child);
        } else if (child instanceof Node) {
            parent.appendChild(child);
        } else {
            parent.appendChild(document.createTextNode(String(child)));
        }
    }
}

/** An inline SVG from markup written in this repository — never from anything a person or a server sent. */
export function svg(markup: string): Element {
    const template = document.createElement('template');
    template.innerHTML = markup.trim();

    return template.content.firstElementChild!;
}
